import { Fragment, RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material"
import {
  ColumnDef,
  ColumnFiltersState,
  ColumnResizeMode,
  ColumnSizingState,
  ExpandedState,
  ExpandedStateList,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  Updater,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table"
import _ from "lodash"
import { SnackbarKey } from "notistack"
import { ErrorBoundary } from "react-error-boundary"

import { buildViewEventData } from "../../../analytics/viewMetadata"
import {
  COLUMN_PARSE_TYPES,
  ColumnId,
  createAnnotationColumn,
  DEFAULT_COLUMN_ORDER,
  DEFAULT_COLUMN_VISIBILITY,
  getAnnotationKeyCols,
  getColumnMetadata,
  INPUT_PARSERS,
  GET_JOB_COLUMNS,
  JobTableColumn,
  PINNED_COLUMNS,
  StandardColumnId,
  toAnnotationColId,
  toColId,
  JobColumnsOptions,
  LookoutColumnOrder,
} from "../../../common/jobsTableColumns"
import { formatColumnList } from "../../../common/jobsTableFormatters"
import {
  LookoutColumnFilter,
  changedFilterColumnIds,
  diffOfKeys,
  getFiltersForRowsSelection,
  PendingData,
  pendingDataForAllVisibleData,
  pruneUnsatisfiedFilters,
  updaterToValue,
} from "../../../common/jobsTableUtils"
import { fromRowId, RowId } from "../../../common/reactTableUtils"
import { EmptyInputError, ParseError } from "../../../common/resourceUtils"
import { getErrorMessage, useStableRouter, waitMs } from "../../../common/utils"
import { AlertErrorFallback } from "../../../components/AlertErrorFallback"
import { useFormatNumberWithUserSettings } from "../../../components/hooks/formatNumberWithUserSettings"
import {
  useFormatIsoTimestampWithUserSettings,
  useDisplayedTimeZoneWithUserSettings,
} from "../../../components/hooks/formatTimeWithUserSettings"
import { useCloseSnackbar, useCustomSnackbar, useUndoableSnackbar } from "../../../components/hooks/useCustomSnackbar"
import { columnIsAggregatable, useFetchJobsTableData } from "../../../components/hooks/useJobsTableData"
import { CommandSpec } from "../../../config"
import { isJobGroupRow, JobRow, JobTableRow } from "../../../models/jobsTableModels"
import { AggregateType, Job, JobFiltersWithExcludes, JobId, Match } from "../../../models/lookoutModels"
import { CustomViewsService } from "../../../services/lookout/CustomViewsService"
import {
  JobsTablePreferences,
  JobsTablePreferencesService,
} from "../../../services/lookout/JobsTablePreferencesService"
import { useGroupJobs } from "../../../services/lookout/useGroupJobs"

import { JobsTableActionBar } from "./JobsTableActionBar"
import { HeaderCell } from "./JobsTableCell"
import styles from "./JobsTableContainer.module.css"
import { JobsTableRow } from "./JobsTableRow"
import { Sidebar } from "./sidebar/Sidebar"

const PAGE_SIZE_OPTIONS = [5, 25, 50, 100]

interface JobsTableContainerProps {
  debug: boolean
  autoRefreshMs: number | undefined
  commandSpecs: CommandSpec[]
}

function toLookoutOrder(sortingState: SortingState): LookoutColumnOrder {
  if (sortingState.length === 0) {
    return {
      id: StandardColumnId.JobID,
      direction: "DESC",
    }
  }
  return {
    id: sortingState[0].id,
    direction: sortingState[0].desc ? "DESC" : "ASC",
  }
}

function fromLookoutOrder(lookoutOrder: LookoutColumnOrder): SortingState {
  return [
    {
      id: lookoutOrder.id,
      desc: lookoutOrder.direction === "DESC",
    },
  ]
}

export const JobsTableContainer = ({ debug, autoRefreshMs, commandSpecs }: JobsTableContainerProps) => {
  const openSnackbar = useCustomSnackbar()
  const openUndoableSnackbar = useUndoableSnackbar()
  const closeSnackbar = useCloseSnackbar()
  const groupJobs = useGroupJobs()

  const router = useStableRouter()
  const jobsTablePreferencesService = useMemo(() => new JobsTablePreferencesService(router), [router])
  const customViewsService = useMemo(() => new CustomViewsService(), [])
  const initialPrefs = useMemo(() => jobsTablePreferencesService.getUserPrefs(), [])

  const formatIsoTimestamp = useFormatIsoTimestampWithUserSettings()
  const displayedTimeZoneName = useDisplayedTimeZoneWithUserSettings()
  const formatNumber = useFormatNumberWithUserSettings()

  const jobColumnsOptions = useMemo<JobColumnsOptions>(
    () => ({ formatIsoTimestamp, displayedTimeZoneName, formatNumber }),
    [formatIsoTimestamp, displayedTimeZoneName, formatNumber],
  )

  // Columns
  const [allColumns, setAllColumns] = useState<JobTableColumn[]>(
    GET_JOB_COLUMNS(jobColumnsOptions).concat(...initialPrefs.annotationColumnKeys.map(createAnnotationColumn)),
  )
  useEffect(() => {
    setAllColumns(
      GET_JOB_COLUMNS(jobColumnsOptions).concat(...initialPrefs.annotationColumnKeys.map(createAnnotationColumn)),
    )
  }, [jobColumnsOptions])

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(initialPrefs.visibleColumns)
  const visibleColumnIds = useMemo(
    () =>
      Object.keys(columnVisibility)
        .map(toColId)
        .filter((colId) => columnVisibility[colId]),
    [columnVisibility],
  )
  const columnResizeMode = useMemo(() => "onChange" as ColumnResizeMode, [])
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(initialPrefs.columnSizing ?? {})
  const [columnOrder, setColumnOrder] = useState<ColumnId[]>(initialPrefs.columnOrder)

  // Grouping
  const [grouping, setGrouping] = useState<ColumnId[]>(initialPrefs.groupedColumns)

  // Expanding
  const [expanded, setExpanded] = useState<ExpandedStateList>(initialPrefs.expandedState)

  // Selecting
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({})
  const [lastSelectedRow, setLastSelectedRow] = useState<Row<JobTableRow> | undefined>(undefined)

  // Sidebar
  const [sidebarJobId, setSidebarJobId] = useState<JobId | undefined>(initialPrefs.sidebarJobId)
  const [sidebarWidth, setSidebarWidth] = useState<number>(initialPrefs.sidebarWidth ?? 600)

  // Pagination
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPrefs.pageIndex,
    pageSize: initialPrefs.pageSize,
  })
  const { pageIndex, pageSize } = useMemo(() => pagination, [pagination])

  const [autoRefresh, setAutoRefresh] = useState<boolean>(
    initialPrefs.autoRefresh === undefined ? true : initialPrefs.autoRefresh,
  )

  const onAutoRefreshChange = (autoRefresh: boolean) => {
    setAutoRefresh(autoRefresh)
  }

  // Filtering
  const [columnFilterState, setColumnFilterState] = useState<ColumnFiltersState>(
    // Filters may arrive from the query string with their prerequisites missing, e.g. from a stale
    // or hand-edited link
    pruneUnsatisfiedFilters(initialPrefs.filters).filters,
  )
  // Tracks the current filter state for callbacks which may outlive the render they were created in,
  // such as the undo action of a snackbar
  const columnFilterStateRef = useRef(columnFilterState)
  columnFilterStateRef.current = columnFilterState
  // Undo actions which are still on screen, each with the columns its undo action would overwrite
  const outstandingUndoActionsRef = useRef<{ snackbarKey: SnackbarKey; restoredColumnIds: string[] }[]>([])
  const [lookoutFilters, setLookoutFilters] = useState<LookoutColumnFilter[]>([]) // Parsed later
  const [columnMatches, setColumnMatches] = useState<Record<string, Match>>(initialPrefs.columnMatches)
  const [parseErrors, setParseErrors] = useState<Record<string, string | undefined>>({})
  const [textFieldRefs, setTextFieldRefs] = useState<Record<string, RefObject<HTMLInputElement | undefined>>>({})
  const [activeJobSets, setActiveJobSets] = useState<boolean>(
    initialPrefs.activeJobSets === undefined ? false : initialPrefs.activeJobSets,
  )
  const [lastTransitionTimeAggregate, setLastTransitionTimeAggregate] = useState<AggregateType>(
    initialPrefs.lastTransitionTimeAggregate || "average",
  )

  // Sorting
  const [lookoutOrder, setLookoutOrder] = useState<LookoutColumnOrder>(initialPrefs.order)
  const [sorting, setSorting] = useState<SortingState>(fromLookoutOrder(lookoutOrder))

  // Data
  const { data, jobInfoMap, rowsToFetch, setRowsToFetch } = useFetchJobsTableData({
    groupedColumns: grouping,
    visibleColumns: visibleColumnIds,
    expandedState: expanded,
    paginationState: pagination,
    lookoutOrder: lookoutOrder,
    lookoutFilters: lookoutFilters,
    activeJobSets: activeJobSets,
    columnMatches: columnMatches,
    allColumns,
    selectedRows,
    updateSelectedRows: setSelectedRows,
    groupJobs,
    openSnackbar,
    lastTransitionTimeAggregate,
  })

  // Custom views (cache)
  const [customViews, setCustomViews] = useState<string[]>(customViewsService.getAllViews())

  // Check if there are grouped columns in initial configuration, and if so enable count column
  useEffect(() => {
    if (grouping.length > 0) {
      setColumnVisibility({
        ...columnVisibility,
        [StandardColumnId.Count]: true,
      })
    }
  }, [])

  // Retrieve data for any expanded rows from initial query param state
  useEffect(() => {
    const rowsToFetch: PendingData[] = [
      { parentRowId: "ROOT", skip: pagination.pageIndex * pagination.pageSize },
      ...Object.keys(initialPrefs.expandedState).map((rowId) => ({ parentRowId: rowId as RowId, skip: 0 })),
    ]
    setRowsToFetch(rowsToFetch)
  }, [initialPrefs])

  // Find the job details for the selected job
  const sidebarJobDetails = useMemo(
    () => (sidebarJobId !== undefined ? jobInfoMap.get(sidebarJobId) : undefined),
    [sidebarJobId, jobInfoMap],
  )

  const prefsFromState = (): JobsTablePreferences => ({
    groupedColumns: grouping,
    expandedState: expanded,
    columnOrder,
    pageIndex,
    pageSize,
    order: lookoutOrder,
    columnSizing: columnSizing,
    filters: columnFilterState,
    columnMatches: columnMatches,
    annotationColumnKeys: getAnnotationKeyCols(allColumns),
    visibleColumns: columnVisibility,
    sidebarJobId: sidebarJobId,
    sidebarWidth: sidebarWidth,
    activeJobSets: activeJobSets,
    autoRefresh: autoRefresh,
    lastTransitionTimeAggregate,
  })

  const setTextFields = (filters: ColumnFiltersState) => {
    const filterMap = Object.fromEntries(filters.map((f) => [f.id, f]))
    for (const [id, ref] of Object.entries(textFieldRefs)) {
      let value = ""
      if (id in filterMap) {
        const filter = filterMap[id]
        value = filter.value as string
      }
      if (ref.current) {
        ref.current.value = value
      }
    }
  }

  const setToFirstPage = () => {
    setPagination({
      pageIndex: 0,
      pageSize: pageSize,
    })
  }

  const loadPrefs = (prefs: JobsTablePreferences) => {
    setGrouping(prefs.groupedColumns)
    setExpanded(prefs.expandedState)
    setPagination({
      pageIndex: 0,
      pageSize: prefs.pageSize,
    })
    setLookoutOrder(prefs.order)
    setSorting(fromLookoutOrder(prefs.order))
    setColumnSizing(prefs.columnSizing ?? {})
    const { filters: prunedFilters } = pruneUnsatisfiedFilters(prefs.filters)
    setColumnFilterState(prunedFilters)
    setLookoutFilters(parseLookoutFilters(prunedFilters))
    setColumnMatches(prefs.columnMatches)
    const cols = GET_JOB_COLUMNS(jobColumnsOptions).concat(...prefs.annotationColumnKeys.map(createAnnotationColumn))
    setAllColumns(cols)
    setColumnOrder(prefs.columnOrder)
    setColumnVisibility(prefs.visibleColumns)
    setSidebarJobId(prefs.sidebarJobId)
    setSidebarWidth(prefs.sidebarWidth ?? 600)
    setSelectedRows({})
    if (prefs.activeJobSets !== undefined) {
      setActiveJobSets(prefs.activeJobSets)
    }
    if (prefs.autoRefresh !== undefined) {
      onAutoRefreshChange(prefs.autoRefresh)
    }
    if (prefs.lastTransitionTimeAggregate !== undefined) {
      setLastTransitionTimeAggregate(prefs.lastTransitionTimeAggregate)
    }

    // Have to manually set text fields to the filter values since they are uncontrolled
    setTextFields(prunedFilters)

    // Load data
    setRowsToFetch(pendingDataForAllVisibleData(prefs.expandedState, data, prefs.pageSize))
  }

  // Update query params with table state
  useEffect(() => {
    jobsTablePreferencesService.saveNewPrefs(prefsFromState())
  }, [
    columnOrder,
    grouping,
    expanded,
    pageIndex,
    pageSize,
    sorting,
    columnSizing,
    columnFilterState,
    columnMatches,
    allColumns,
    columnVisibility,
    selectedRows,
    sidebarJobId,
    sidebarWidth,
    activeJobSets,
    autoRefresh,
    lastTransitionTimeAggregate,
  ])

  const addCustomView = (name: string) => {
    const prefs = prefsFromState()
    customViewsService.saveView(name, prefs)
    setCustomViews(customViewsService.getAllViews())
  }

  const deleteCustomView = (name: string) => {
    customViewsService.deleteView(name)
    setCustomViews(customViewsService.getAllViews())
  }

  const loadCustomView = (name: string) => {
    try {
      const prefs = customViewsService.getView(name)
      setToFirstPage()
      loadPrefs(prefs)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(getErrorMessage(e))
      openSnackbar("Failed to load custom view", "error")
    }
  }

  const getViewEventData = (name: string): Record<string, string> => {
    try {
      const prefs = customViewsService.getView(name)
      return buildViewEventData(name, prefs)
    } catch {
      return { viewName: name }
    }
  }

  const getCurrentViewEventData = (name?: string): Record<string, string> => {
    const prefs = prefsFromState()
    return buildViewEventData(name ?? "", prefs)
  }

  const onRefresh = () => {
    setSelectedRows({})
    setRowsToFetch(pendingDataForAllVisibleData(expanded, data, pageSize, pageIndex * pageSize))
  }

  const savedOnRefreshCallback = useRef<() => void>(undefined)

  const [autoRefreshIntervalTimer, setAutoRefreshIntervalTimer] = useState<NodeJS.Timeout | undefined>(undefined)

  useEffect(() => {
    savedOnRefreshCallback.current = onRefresh
  })

  useEffect(() => {
    function clearTimer() {
      if (autoRefreshIntervalTimer) {
        clearInterval(autoRefreshIntervalTimer)
        setAutoRefreshIntervalTimer(undefined)
      }
    }

    if (autoRefreshMs === undefined || !autoRefresh) {
      clearTimer()
      return () => {
        return
      }
    } else {
      const tmr = setInterval(() => {
        if (savedOnRefreshCallback.current) {
          savedOnRefreshCallback.current()
        }
      }, autoRefreshMs)
      setAutoRefreshIntervalTimer(tmr)
      return clearTimer
    }
  }, [autoRefresh, autoRefreshMs])

  const onColumnVisibilityChange = (colIdToToggle: ColumnId) => {
    // Refresh if we make a new aggregate column visible
    let shouldRefresh = false
    if (columnIsAggregatable(colIdToToggle) && grouping.length > 0 && !visibleColumnIds.includes(colIdToToggle)) {
      shouldRefresh = true
    }
    const isBeingHidden = Boolean(columnVisibility[colIdToToggle])
    setColumnVisibility({
      ...columnVisibility,
      [colIdToToggle]: !columnVisibility[colIdToToggle],
    })

    // Hiding a column also removes its filter input, so clear any filter on it to avoid leaving a
    // filter applied with no means of removing it. This refetches the data itself.
    if (isBeingHidden && columnFilterState.some(({ id }) => id === colIdToToggle)) {
      onFilterChange(columnFilterState.filter(({ id }) => id !== colIdToToggle))
    } else if (shouldRefresh) {
      setRowsToFetch(pendingDataForAllVisibleData(expanded, data, pageSize, pageIndex * pageSize))
    }
  }

  const resetColumnConfiguration = useCallback(() => {
    const annotationColumnIds = getAnnotationKeyCols(allColumns).map((key) => toAnnotationColId(key))
    const mustRemainVisible = [
      ...grouping,
      ...(grouping.length > 0 ? [StandardColumnId.Count] : []),
      ...columnFilterState.map(({ id }) => toColId(id)),
      toColId(lookoutOrder.id),
    ]
    const newColumnVisibility = {
      ...DEFAULT_COLUMN_VISIBILITY,
      ...Object.fromEntries(annotationColumnIds.map((colId) => [colId, false])),
      ...Object.fromEntries(mustRemainVisible.map((colId) => [colId, true])),
    }
    const revealsNewAggregateColumn =
      grouping.length > 0 &&
      Object.entries(newColumnVisibility).some(([rawColId, isVisible]) => {
        const colId = toColId(rawColId)
        return isVisible && columnIsAggregatable(colId) && !visibleColumnIds.includes(colId)
      })

    setColumnOrder([...DEFAULT_COLUMN_ORDER, ...annotationColumnIds])
    setColumnVisibility(newColumnVisibility)
    setColumnSizing({})
    jobsTablePreferencesService.clearLegacyColumnSizingFromLocalStorage()
    if (revealsNewAggregateColumn) {
      setRowsToFetch(pendingDataForAllVisibleData(expanded, data, pageSize, pageIndex * pageSize))
    }

    const previousColumnOrder = columnOrder
    const previousColumnVisibility = columnVisibility
    const previousColumnSizing = columnSizing
    openUndoableSnackbar("Column configuration reset to defaults.", () => {
      setColumnOrder(previousColumnOrder)
      setColumnVisibility(previousColumnVisibility)
      setColumnSizing(previousColumnSizing)
    })
  }, [
    allColumns,
    grouping,
    columnFilterState,
    lookoutOrder,
    columnOrder,
    columnVisibility,
    columnSizing,
    visibleColumnIds,
    expanded,
    data,
    pageSize,
    pageIndex,
    jobsTablePreferencesService,
    setRowsToFetch,
    openUndoableSnackbar,
  ])

  const colIsVisible = (column: ColumnId): boolean => {
    return column in columnVisibility && columnVisibility[column]
  }

  const addAnnotationCol = (annotationKey: string) => {
    for (const col of allColumns) {
      if (col.id === toAnnotationColId(annotationKey)) {
        throw new Error(`annotation column "${annotationKey}" already exists`)
      }
    }

    const annotationCol = createAnnotationColumn(annotationKey)
    const annotationColId = toColId(annotationCol.id)
    const newCols = allColumns.concat([annotationCol])
    setColumnOrder((prev) => [...prev, annotationColId])
    setAllColumns(newCols)
    if (!colIsVisible(annotationColId)) {
      onColumnVisibilityChange(annotationColId)
    }
  }

  const removeAnnotationCol = (colId: ColumnId) => {
    const filtered = allColumns.filter((col) => col.id !== colId)
    if (filtered.length === allColumns.length) {
      throw new Error(`column "${colId}" was not removed`)
    }
    setColumnOrder((prev) => prev.filter((prevId) => prevId !== colId))
    setAllColumns(filtered)
    setColumnVisibility((prev) => {
      const { [colId]: _, ...rest } = prev
      return rest
    })
    onFilterChange((columnFilters) => {
      return columnFilters.filter((columnFilter) => columnFilter.id !== colId)
    })
  }

  const editAnnotationCol = (colId: ColumnId, annotationKey: string) => {
    let index = -1
    for (let i = 0; i < allColumns.length; i++) {
      if (allColumns[i].id === colId) {
        index = i
      }
    }
    if (index === -1) {
      throw new Error(`column "${colId}" not found`)
    }

    const oldColId = toColId(allColumns[index].id)

    // Make old column not visible
    if (colIsVisible(oldColId)) {
      onColumnVisibilityChange(oldColId)
    }

    allColumns[index] = createAnnotationColumn(annotationKey)
    const newColId = toColId(allColumns[index].id)

    // Make new column visible
    setAllColumns([...allColumns])
    if (!colIsVisible(toColId(newColId))) {
      onColumnVisibilityChange(toColId(newColId))
    }

    // Change column ID in ordering
    setColumnOrder((prev) => prev.map((prevId) => (prevId === oldColId ? newColId : prevId)))
  }

  const onGroupingChange = useCallback(
    (newGroups: ColumnId[]) => {
      setToFirstPage()
      // Reset currently expanded/selected when grouping changes
      setSelectedRows({})
      setSidebarJobId(undefined)
      setExpanded({})

      const baseColumnVisibility = {
        ...columnVisibility,
        [StandardColumnId.Count]: false,
      }
      if (newGroups.length > 0) {
        baseColumnVisibility[StandardColumnId.Count] = true
      }
      // Check all grouping columns are displayed
      setColumnVisibility(newGroups.reduce((a, s) => ({ ...a, [s]: true }), baseColumnVisibility))

      setGrouping([...newGroups])

      // Refetch the root data
      setRowsToFetch([{ parentRowId: "ROOT", skip: 0 }])
    },
    [allColumns, columnVisibility],
  )

  const onRootPaginationChange = useCallback(
    (updater: Updater<PaginationState>) => {
      const newPagination = updaterToValue(updater, pagination)
      // Reset currently expanded/selected when grouping changes
      // TODO: Consider allowing rows to be selected across pages?
      setSelectedRows({})
      setSidebarJobId(undefined)
      setExpanded({})
      setPagination(newPagination)

      // Refetch the root data
      setRowsToFetch([{ parentRowId: "ROOT", skip: newPagination.pageIndex * pageSize }])
    },
    [pagination, pageSize],
  )

  const onLoadMoreSubRows = useCallback((rowId: RowId, skip: number) => {
    setRowsToFetch([{ parentRowId: rowId, skip, append: true }])
  }, [])

  const onExpandedChange = useCallback(
    (updater: Updater<ExpandedState>) => {
      const newExpandedOrBool = updaterToValue(updater, expanded)
      const newExpandedState =
        typeof newExpandedOrBool === "boolean"
          ? _.fromPairs(table.getRowModel().flatRows.map((r) => [r.id, true]))
          : newExpandedOrBool
      const [newlyExpanded] = diffOfKeys<RowId>(newExpandedState, expanded)
      setExpanded(newExpandedState)

      // Fetch sub-rows for expanded rows
      setRowsToFetch(newlyExpanded.map((rowId) => ({ parentRowId: rowId, skip: 0 })))
    },
    [expanded],
  )

  const onSelectedRowChange = (updater: Updater<RowSelectionState>) => {
    const newSelectedRows = updaterToValue(updater, selectedRows)
    setSelectedRows(newSelectedRows)
  }

  const setParseError = (colId: string, error: string | undefined) => {
    setParseErrors((old) => {
      const newParseErrors = { ...old }
      newParseErrors[colId] = error
      return newParseErrors
    })
  }

  const parseLookoutFilters = (state: ColumnFiltersState): LookoutColumnFilter[] => {
    const lookoutFilters: LookoutColumnFilter[] = []
    for (const columnFilter of state) {
      if (!(columnFilter.id in COLUMN_PARSE_TYPES)) {
        lookoutFilters.push({
          id: columnFilter.id,
          value: columnFilter.value as string | string[] | number | number[],
        })
        continue
      }
      const parseType = COLUMN_PARSE_TYPES[columnFilter.id]
      const parser = INPUT_PARSERS[parseType]
      const raw = columnFilter.value as string
      try {
        const value = parser(raw.trim())
        setParseError(columnFilter.id, undefined)
        lookoutFilters.push({
          id: columnFilter.id,
          value: value,
        })
      } catch (e) {
        if (e instanceof EmptyInputError) {
          setParseError(columnFilter.id, undefined)
        } else if (e instanceof ParseError) {
          setParseError(columnFilter.id, e.message)
        }
      }
    }
    return lookoutFilters
  }

  useEffect(() => {
    setLookoutFilters(parseLookoutFilters(columnFilterState))
  }, [])

  const setTextFieldRef = (columnId: string, ref: RefObject<HTMLInputElement | undefined>) => {
    setTextFieldRefs((old) => {
      const newState = { ...old }
      newState[columnId] = ref
      return newState
    })
  }

  const applyFilterState = (newFilterState: ColumnFiltersState, syncTextFields = false) => {
    setToFirstPage()
    setLookoutFilters(parseLookoutFilters(newFilterState))
    setColumnFilterState(newFilterState)
    if (syncTextFields) {
      // The text field inputs are uncontrolled, so they only need updating when the filter state
      // changes other than by the user typing into them
      setTextFields(newFilterState)
    }
    setSelectedRows({})
    setSidebarJobId(undefined)
    setRowsToFetch(pendingDataForAllVisibleData(expanded, data, pageSize))
  }

  const onFilterChange = (updater: Updater<ColumnFiltersState>, syncTextFields = false) => {
    const previousFilterState = columnFilterStateRef.current
    const requestedFilterState = updaterToValue(updater, previousFilterState)

    // Removing a filter can leave filters on dependent columns applied but unreachable, since their
    // inputs are replaced by a message prompting for the prerequisite filter. Such filters are
    // dropped so that no filter can be in effect without a control to remove it.
    const { filters: newFilterState, removedColumnIds } = pruneUnsatisfiedFilters(requestedFilterState)

    if (_.isEqual(newFilterState, previousFilterState)) {
      return
    }

    // An outstanding undo action would overwrite the filters on its own columns with the values they
    // held before its cascade. Once the user has edited one of those columns themselves, undoing
    // would discard that newer edit, so the offer is withdrawn.
    const changedColumnIds = changedFilterColumnIds(previousFilterState, newFilterState)
    outstandingUndoActionsRef.current = outstandingUndoActionsRef.current.filter(
      ({ snackbarKey, restoredColumnIds }) => {
        if (_.intersection(restoredColumnIds, changedColumnIds).length === 0) {
          return true
        }
        closeSnackbar(snackbarKey)
        return false
      },
    )

    // Any pruned filter may have had a text input, which must be cleared along with it
    applyFilterState(newFilterState, syncTextFields || removedColumnIds.length > 0)

    if (removedColumnIds.length > 0) {
      // Undoing must restore the filters which were pruned, but a pruned filter cannot stand on its
      // own: it was pruned precisely because the edit left its prerequisite unsatisfied. So the
      // columns the edit itself changed are restored too, and only those. Filters on any other
      // column are left as they are, so that changes made while the undo action was available are
      // not discarded.
      const restoredColumnIds = _.union(
        changedFilterColumnIds(previousFilterState, requestedFilterState),
        removedColumnIds,
      )
      const restoredFilters = previousFilterState.filter(({ id }) => restoredColumnIds.includes(id))

      const removedNames = removedColumnIds.map((colId) => {
        const column = allColumns.find(({ id }) => id === colId)
        return (column ? getColumnMetadata(column).displayName : undefined) ?? colId
      })
      const snackbarKey = openUndoableSnackbar(
        `${formatColumnList(removedNames)} ${removedNames.length === 1 ? "filter" : "filters"} cleared, as ${
          removedNames.length === 1 ? "it requires" : "they require"
        } a filter on another column.`,
        () =>
          onFilterChange(
            (current) => [...current.filter(({ id }) => !restoredColumnIds.includes(id)), ...restoredFilters],
            true,
          ),
        {
          // However the snackbar goes away, whether undone, dismissed or auto-hidden, its undo
          // action is no longer available and so must no longer be tracked
          onExited: (_node, key) => {
            outstandingUndoActionsRef.current = outstandingUndoActionsRef.current.filter(
              (undoAction) => undoAction.snackbarKey !== key,
            )
          },
        },
      )
      outstandingUndoActionsRef.current = [...outstandingUndoActionsRef.current, { snackbarKey, restoredColumnIds }]
    }
  }

  const onColumnMatchChange = (columnId: string, newMatch: Match) => {
    const newColumnMatches: Record<string, Match> = {
      ...columnMatches,
      [columnId]: newMatch,
    }
    setColumnMatches(newColumnMatches)
    onFilterChange([...columnFilterState])
    setRowsToFetch(pendingDataForAllVisibleData(expanded, data, pageSize))
  }

  const onSortingChange = (updater: Updater<SortingState>) => {
    setToFirstPage()
    const newSortingState = updaterToValue(updater, sorting)
    setLookoutOrder(toLookoutOrder(newSortingState))
    setSorting(newSortingState)
    // Refetch any expanded subgroups, and root data with updated sorting params
    setRowsToFetch(pendingDataForAllVisibleData(expanded, data, pageSize))
  }

  const onColumnSizingChange = useCallback(
    (updater: Updater<ColumnSizingState>) => {
      const newColumnSizing = updaterToValue(updater, columnSizing)
      setColumnSizing(newColumnSizing)
    },
    [columnSizing],
  )

  const onLastTransitionTimeAggregateChange = useCallback(
    (lastTransitionTimeAggregate: AggregateType) => {
      setLastTransitionTimeAggregate(lastTransitionTimeAggregate)
      setRowsToFetch(pendingDataForAllVisibleData(expanded, data, pageSize))
    },
    [setRowsToFetch],
  )

  const toggleSidebarForJobRow = (jobRow: JobRow) => {
    const clickedJob = jobRow as Job
    const jobId = clickedJob.jobId
    // Deselect if clicking on a job row that's already shown in the sidebar
    setSidebarJobId(jobId === sidebarJobId ? undefined : jobId)
  }
  const sideBarClose = () => setSidebarJobId(undefined)

  const filteredData = data ?? []

  const selectedItemsFilters: JobFiltersWithExcludes[] = useMemo(
    () => getFiltersForRowsSelection(filteredData, selectedRows, lookoutFilters, columnMatches),
    [filteredData, selectedRows, lookoutFilters, columnMatches],
  )

  const table = useReactTable({
    data: filteredData,
    columns: allColumns,
    state: {
      grouping,
      expanded,
      pagination,
      columnFilters: columnFilterState,
      rowSelection: selectedRows,
      columnPinning: {
        left: PINNED_COLUMNS,
      },
      columnVisibility,
      sorting,
      columnOrder,
      columnSizing,
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.rowId,
    getSubRows: (row) => (isJobGroupRow(row) && row.subRows) || undefined,
    columnResizeMode: columnResizeMode,

    // Selection
    enableRowSelection: true,
    enableMultiRowSelection: true,
    enableSubRowSelection: true,
    onRowSelectionChange: onSelectedRowChange,

    // Grouping
    manualGrouping: true,
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: onExpandedChange,
    autoResetExpanded: false,
    manualExpanding: false,

    // Pagination
    manualPagination: true,
    paginateExpandedRows: true,
    onPaginationChange: onRootPaginationChange,
    getPaginationRowModel: getPaginationRowModel(),

    // Filtering
    onColumnFiltersChange: onFilterChange,
    manualFiltering: true,

    // Sorting
    onSortingChange: onSortingChange,

    // Column resizing
    onColumnSizingChange: onColumnSizingChange,
  })

  const clearFilters = () => {
    table.resetColumnFilters(true)
    for (const [, ref] of Object.entries(textFieldRefs)) {
      if (ref.current) {
        ref.current.value = ""
      }
    }
    onFilterChange([])
    setParseErrors({})
  }

  const clearSorting = () => {
    onSortingChange([])
  }

  const customSortingApplied = sorting.length > 0

  const clearGroups = () => {
    // Set grouping to an empty array to clear all groups
    onGroupingChange([])

    // Reset expanded states as no groups will exist
    setExpanded({})

    // Clear any related UI states if necessary
    // For example, resetting selection
    setSelectedRows({})
  }

  const shiftSelectRow = (row: Row<JobTableRow>) => {
    if (lastSelectedRow === undefined || row.depth !== lastSelectedRow.depth) {
      return
    }

    const rowIndex = table.getRowModel().rows.indexOf(row)
    const lastSelectedRowIndex = table.getRowModel().rows.indexOf(lastSelectedRow)

    const selectedValue = lastSelectedRow.getIsSelected()
    const rowSelectionMask: RowSelectionState = table
      .getRowModel()
      .rows.slice(Math.min(rowIndex, lastSelectedRowIndex), Math.max(rowIndex, lastSelectedRowIndex) + 1)
      .reduce<RowSelectionState>((acc, { id, depth }) => {
        if (depth === row.depth) {
          acc[id] = selectedValue
        }
        return acc
      }, {})

    table.setRowSelection((prev) => ({ ...prev, ...rowSelectionMask }))
  }

  const selectRow = async (row: Row<JobTableRow>, singleSelect: boolean) => {
    setLastSelectedRow(row)
    const isSelected = row.getIsSelected()
    if (singleSelect) {
      table.toggleAllRowsSelected(false)
      await waitMs(1)
    }
    row.toggleSelected(!isSelected)
  }

  const topLevelRows = table.getRowModel().rows.filter((row) => row.depth === 0)
  let columnsForSelect = allColumns
  if (grouping.length === 0) {
    columnsForSelect = columnsForSelect.filter((col) => col.id !== StandardColumnId.Count)
  }
  const columnStyle = {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    flex: 1,
  }

  const filterColumns = useMemo(() => columnFilterState.map(({ id }) => toColId(id)), [columnFilterState])
  const sortColumns = useMemo(() => [toColId(lookoutOrder.id)], [lookoutOrder])

  return (
    <Box sx={{ display: "flex", flexDirection: "row", height: "100%", width: "100%" }}>
      <Box sx={{ ...columnStyle, marginX: "0.5em", minWidth: 0 }}>
        <Box sx={{ ...columnStyle, marginY: "0.5em", minHeight: 0 }}>
          <ErrorBoundary FallbackComponent={AlertErrorFallback}>
            <JobsTableActionBar
              isLoading={rowsToFetch.length > 0}
              allColumns={columnsForSelect}
              groupedColumns={grouping}
              filterColumns={filterColumns}
              sortColumns={sortColumns}
              visibleColumns={visibleColumnIds}
              columnOrder={columnOrder}
              setColumnOrder={setColumnOrder}
              selectedItemFilters={selectedItemsFilters}
              customViews={customViews}
              activeJobSets={activeJobSets}
              onActiveJobSetsChanged={(newVal) => {
                setActiveJobSets(newVal)
                setToFirstPage()
                setSelectedRows({})
                setRowsToFetch(pendingDataForAllVisibleData(expanded, data, pageSize))
              }}
              onRefresh={onRefresh}
              autoRefresh={autoRefresh}
              onAutoRefreshChange={autoRefreshMs === undefined ? undefined : onAutoRefreshChange}
              onAddAnnotationColumn={addAnnotationCol}
              onRemoveAnnotationColumn={removeAnnotationCol}
              onEditAnnotationColumn={editAnnotationCol}
              onGroupsChanged={onGroupingChange}
              toggleColumnVisibility={onColumnVisibilityChange}
              onResetColumnConfiguration={resetColumnConfiguration}
              onClearFilters={clearFilters}
              onClearSorting={clearSorting}
              customSortingApplied={customSortingApplied}
              onClearGroups={clearGroups}
              onAddCustomView={addCustomView}
              onDeleteCustomView={deleteCustomView}
              onLoadCustomView={loadCustomView}
              getViewEventData={getViewEventData}
              getCurrentViewEventData={getCurrentViewEventData}
            />
          </ErrorBoundary>
          <ErrorBoundary FallbackComponent={AlertErrorFallback}>
            <TableContainer component={Paper} style={{ height: "100%" }}>
              <Table
                stickyHeader
                sx={{ tableLayout: "fixed" }}
                aria-label="Jobs table"
                style={{
                  width: table.getCenterTotalSize(),
                  borderLeft: "1px solid #cccccc",
                  maxHeight: "100%",
                }}
              >
                <TableHead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <HeaderCell
                          header={header}
                          key={header.id}
                          parseError={header.id in parseErrors ? parseErrors[header.id] : undefined}
                          columnResizeMode={columnResizeMode}
                          deltaOffset={table.getState().columnSizingInfo.deltaOffset ?? 0}
                          columnMatches={columnMatches}
                          onColumnMatchChange={onColumnMatchChange}
                          onSetTextFieldRef={(ref) => {
                            setTextFieldRef(header.id, ref)
                          }}
                          groupedColumns={grouping}
                          lastTransitionTimeAggregate={lastTransitionTimeAggregate}
                          onLastTransitionTimeAggregateChange={onLastTransitionTimeAggregateChange}
                        />
                      ))}
                    </TableRow>
                  ))}
                </TableHead>

                <JobsTableBody
                  dataIsLoading={rowsToFetch.length > 0}
                  columns={table.getVisibleLeafColumns()}
                  topLevelRows={topLevelRows}
                  sidebarJobId={sidebarJobId}
                  onLoadMoreSubRows={onLoadMoreSubRows}
                  onClickRowCheckbox={(row) => selectRow(row, false)}
                  onClickJobRow={toggleSidebarForJobRow}
                  onClickRow={(row) => selectRow(row, true)}
                  onShiftClickRow={shiftSelectRow}
                  onControlClickRow={(row) => selectRow(row, false)}
                  onColumnMatchChange={onColumnMatchChange}
                />
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              rowsPerPageOptions={PAGE_SIZE_OPTIONS}
              // If the total number rows in the database for the current filter
              // is exactly equal to `pageSize`, then this is going to produce a
              // misleading description (e.g., "1-50 of more than 50", even though
              // there are exactly 50 rows).
              count={data.length < pageSize ? pageIndex * pageSize + data.length : -1}
              rowsPerPage={pageSize}
              page={pageIndex}
              onPageChange={(_, page) => table.setPageIndex(page)}
              onRowsPerPageChange={(e) => table.setPageSize(Number(e.target.value))}
              colSpan={table.getVisibleLeafColumns().length}
              showFirstButton={true}
              showLastButton={true}
            />
            {debug && <pre>{JSON.stringify(table.getState(), null, 2)}</pre>}
          </ErrorBoundary>
        </Box>
      </Box>

      {sidebarJobDetails !== undefined && (
        <ErrorBoundary FallbackComponent={AlertErrorFallback}>
          <Sidebar
            job={sidebarJobDetails}
            sidebarWidth={sidebarWidth}
            onClose={sideBarClose}
            onWidthChange={setSidebarWidth}
            commandSpecs={commandSpecs}
          />
        </ErrorBoundary>
      )}
    </Box>
  )
}

interface JobsTableBodyProps {
  dataIsLoading: boolean
  columns: ColumnDef<JobTableRow>[]
  topLevelRows: Row<JobTableRow>[]
  sidebarJobId: JobId | undefined
  onLoadMoreSubRows: (rowId: RowId, skip: number) => void
  onClickRowCheckbox: (row: Row<JobTableRow>) => void
  // Always called if row is a Job row
  onClickJobRow: (jobRow: JobRow) => void
  // Mutually exclusively called depending on key being pressed
  onClickRow: (row: Row<JobTableRow>) => void
  onControlClickRow: (row: Row<JobTableRow>) => void
  onShiftClickRow: (row: Row<JobTableRow>) => void
  onColumnMatchChange: (columnId: string, newMatch: Match) => void
}

const JobsTableBody = ({
  dataIsLoading,
  columns,
  topLevelRows,
  sidebarJobId,
  onLoadMoreSubRows,
  onClickRowCheckbox,
  onClickJobRow,
  onClickRow,
  onControlClickRow,
  onShiftClickRow,
  onColumnMatchChange,
}: JobsTableBodyProps) => {
  const canDisplay = !dataIsLoading && topLevelRows.length > 0
  return (
    <TableBody style={{ overflow: "auto", height: "100%" }}>
      {!canDisplay && (
        <TableRow>
          {dataIsLoading && topLevelRows.length === 0 && (
            <TableCell colSpan={columns.length}>
              <CircularProgress />
            </TableCell>
          )}
          {!dataIsLoading && topLevelRows.length === 0 && (
            <TableCell colSpan={columns.length}>There is no data to display</TableCell>
          )}
        </TableRow>
      )}
      {topLevelRows.map((row) =>
        recursiveRowRender(
          row,
          sidebarJobId,
          onLoadMoreSubRows,
          onClickRowCheckbox,
          onClickJobRow,
          onClickRow,
          onControlClickRow,
          onShiftClickRow,
          onColumnMatchChange,
          dataIsLoading,
        ),
      )}
    </TableBody>
  )
}

const recursiveRowRender = (
  row: Row<JobTableRow>,
  sidebarJobId: JobId | undefined,
  onLoadMoreSubRows: (rowId: RowId, skip: number) => void,
  onClickRowCheckbox: (row: Row<JobTableRow>) => void,
  onClickJobRow: (jobRow: JobRow) => void,
  onClickRow: (row: Row<JobTableRow>) => void,
  onControlClickRow: (row: Row<JobTableRow>) => void,
  onShiftClickRow: (row: Row<JobTableRow>) => void,
  onColumnMatchChange: (columnId: string, newMatch: Match) => void,
  dataIsLoading: boolean,
) => {
  const original = row.original
  const rowIsGroup = isJobGroupRow(original)

  const depthGaugeLevelThicknessPixels = 6
  const isOpenInSidebar = sidebarJobId !== undefined && original.jobId === sidebarJobId

  return (
    <Fragment key={`${row.id}_d${row.depth}`}>
      {/* Render the current row */}
      <JobsTableRow
        row={row}
        isOpenInSidebar={isOpenInSidebar}
        onClick={(e) => {
          if (!rowIsGroup) {
            onClickJobRow(original)
          }
          if (e.shiftKey) {
            onShiftClickRow(row)
          } else if (e.ctrlKey || e.altKey) {
            onControlClickRow(row)
          } else {
            onClickRow(row)
          }
        }}
        onClickRowCheckbox={onClickRowCheckbox}
        onColumnMatchChange={onColumnMatchChange}
      />

      {/* Render any sub rows if expanded */}
      {rowIsGroup &&
        row.getIsExpanded() &&
        row.subRows.map((row) =>
          recursiveRowRender(
            row,
            sidebarJobId,
            onLoadMoreSubRows,
            onClickRowCheckbox,
            onClickJobRow,
            onClickRow,
            onControlClickRow,
            onShiftClickRow,
            onColumnMatchChange,
            dataIsLoading,
          ),
        )}

      {/* Render pagination tools for this expanded row */}
      {rowIsGroup &&
        row.getIsExpanded() &&
        (original.jobCount ?? 0) > original.subRows.length &&
        original.subRows.length > 0 && (
          <TableRow
            className={[styles.rowDepthIndicator, styles.loadMoreRow].join(" ")}
            sx={{ backgroundSize: (row.depth + 1) * depthGaugeLevelThicknessPixels }}
          >
            <TableCell colSpan={row.getVisibleCells().length} align="center" size="small">
              <Button
                size="small"
                variant="text"
                onClick={() => onLoadMoreSubRows(row.id as RowId, original.subRows.length)}
                disabled={dataIsLoading}
                fullWidth
              >
                Load more for{" "}
                {fromRowId(row.id as RowId)
                  .rowIdPartsPath.map(({ type, value }) => `${type}: ${value}`)
                  .join(" > ")}
              </Button>
            </TableCell>
          </TableRow>
        )}
    </Fragment>
  )
}
